import CustomScrollbar from 'app/components/CustomScrollbar';
import { useAnalytics } from 'app/context/Analytics';
import { type FAQPage } from 'app/copied/models/ChatModules';
import useFetchWebSearchResults from 'app/hooks/fetch/useFetchWebSearchResults';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Outlet } from 'react-router-dom';
import { SecondaryIndicationSelector } from 'app/components/IndicationSelector';

import Header, { BackButtonAction } from '../../../components/Header';
import Spinner from '../../../components/Spinner';
import { PosthogEventName } from '../../../copied/constants/PosthogEventName';
import { ChatVersion } from '../../../copied/constants/common';
import { waitForIDs } from '../../../copied/getIds';
import useFetchSearchResults from '../../../hooks/fetch/useFetchSearchResults';
import useFooterLinks from '../../../hooks/useFooterLinks';
import TailorLooking from '../components/TailorLooking';
import SearchInput from '../components/TailorSearchInput';
import TailorSearchResults from '../components/TailorSearchResults';
import useHandleSearchResultClick from '../hooks/useHandleSearchResultClick';
import { useSearch } from '../hooks/useSearch';

export default function TailorSearch() {
  const [results, setResults] = useState<FAQPage[]>([]);
  const { search, setSearch } = useSearch();
  const [deferredSearch] = useDebounce(search, 500);
  const { t } = useTranslation();
  const flags = useFeatureFlags();
  const { captureEvent } = useAnalytics();

  const { data, isLoading: isSearchLoading } = useFetchSearchResults({
    search: deferredSearch,
    suggested: !deferredSearch,
  });
  const { data: webSearchData, isLoading: isWebSearchLoading } = useFetchWebSearchResults({
    query: deferredSearch,
    enabled: flags.webSearchInTailor,
  });

  const isLoading = isSearchLoading || isWebSearchLoading;
  useEffect(() => {
    if (!flags.webSearchInTailor) {
      setResults(data?.results || []);
      return;
    }

    if (data && webSearchData) {
      const combinedResults = [
        ...data.results.slice(0, 2),
        ...webSearchData.results.slice(0, 2),
        ...data.results.slice(2),
        ...webSearchData.results.slice(2),
      ];
      setResults(combinedResults);
    } else if (data) {
      setResults(data.results);
    } else if (webSearchData) {
      setResults(webSearchData.results);
    }
  }, [data, webSearchData, flags.webSearchInTailor]);

  const { secondaryFooterLink } = useFooterLinks();

  const handleResultSelect = useHandleSearchResultClick();

  useEffect(() => {
    captureEvent(PosthogEventName.LoadSearchScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deferredSearch) {
      waitForIDs().then(({ conditionId }) => {
        captureEvent(PosthogEventName.QuerySubmitted, {
          conditionUUID: conditionId,
          version: ChatVersion.v4_0,
          isFlow: false,
          queryTyped: deferredSearch,
          searchSource: 'Search result',
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredSearch]);

  useEffect(() => {
    if (data?.results && !isLoading) {
      captureEvent(PosthogEventName.SearchQueryLoad, {
        results: data.results.map((value) => value.title),
        searchSnippet: data.results.map((value) => !!value.highlight_tag_text),
        result_scores: data.results.map((value) => value.result_rank),
        queryTyped: deferredSearch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.results, deferredSearch, isLoading]);

  return (
    <>
      <CustomScrollbar
        id="ostro-tailor-search-container"
        className="flex flex-col"
        rememberScrollPosition
      >
        <div className="grow px-7 py-5">
          <Header backButtonAction={BackButtonAction.HOME} />

          <SearchInput
            search={search}
            onChange={setSearch}
            className="mt-[15px] mb-[34px]"
          />

          {isLoading ? (
            <div className="grow flex justify-center mt-24">
              <Spinner />
            </div>
          ) : null}

          {!deferredSearch && !isLoading ? <h3 className="mb-3 text-xs">{t('most_requested_resources')}</h3> : null}

          {isLoading ? null : (
            <div className="grow">
              <TailorSearchResults
                showNoResults={true}
                highlightTerms={deferredSearch}
                results={results}
                onSelectResult={(result, index) =>
                  handleResultSelect({
                    result,
                    resultIndex: index + 1,
                    isSuggested: !search,
                  })
                }
              />
            </div>
          )}
        </div>
        <SecondaryIndicationSelector className="px-7 pb-5 grow" />
        <TailorLooking footerLink={secondaryFooterLink} />
      </CustomScrollbar>
      <Outlet />
    </>
  );
}
